import { z } from "zod";

export const planEnum = z.enum(["lite", "plus", "premium", "enterprise"]);
export const subscriptionSourceEnum = z.enum(["stripe", "apple", "google", "test", "web"]);
export const subscriptionStatusEnum = z.enum([
  "trialing",
  "active",
  "past_due",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
]);

export type PlanEnum = z.infer<typeof planEnum>;
export type SubscriptionSourceEnum = z.infer<typeof subscriptionSourceEnum>;
export type SubscriptionStatusEnum = z.infer<typeof subscriptionStatusEnum>;

export const subscriptionSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  organizationIds: z.array(z.string()),
  plan: planEnum,
  extraUsers: z.number(),
  expiredAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  stripeSubscriptionId: z.string().nullable(),
  priceId: z.string().nullable(),
  period: z.enum(["Monthly", "Yearly"]).nullable(),
  status: subscriptionStatusEnum.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  source: subscriptionSourceEnum.nullable(),
  storeProductId: z.string().nullable(),
  storeTransactionId: z.string().nullable(),
  lastStoreEventAt: z.date().nullable(),
});

export type SubscriptionSchema = z.infer<typeof subscriptionSchema>;

export const subscriptionUserInfoSchema = z.object({
  subscription: subscriptionSchema,
  userCount: z.number(),
  invoiceNumber: z.number(),
  invoiceCountThisMonth: z.number(),
  expenseNumber: z.number(),
  billCountThisMonth: z.number(),
  relationNumber: z.number(),
});
export type SubscriptionUserInfoSchema = z.infer<typeof subscriptionUserInfoSchema>;

// Single-plan model: `premium` is the one paid plan with everything unlimited
// (one organization only). `lite` = no access (hard paywall). plus/enterprise are
// legacy values kept only so existing enum data doesn't break.
export const planLimits = {
  user: { lite: 1, plus: 1, premium: Infinity, enterprise: 4 },
  invoice: { lite: 5, plus: 100, premium: Infinity, enterprise: Infinity },
  expense: { lite: 5, plus: 10, premium: Infinity, enterprise: Infinity },
  relation: { lite: 3, plus: 25, premium: Infinity, enterprise: Infinity },
  // Single-org model: exactly one organization per user across every plan.
  organization: { lite: 1, plus: 1, premium: 1, enterprise: 1 },
} as const;

/** Binary access gate for the single-plan model: the user can use the app only
 *  while trialing or active. Everything else (canceled/expired/null) = blocked. */
export const hasActiveAccess = (status: SubscriptionStatusEnum | null | undefined): boolean =>
  status === "trialing" || status === "active";

/** Length of the card-less free trial granted to web signups, in days. */
export const WEB_TRIAL_DAYS = 3;

/**
 * A row is still a virgin signup row (created by the auth hook): no status, no
 * expiry, and no billing provider has ever touched it. Such users can start the
 * card-less WEB_TRIAL_DAYS web trial (granted locally by `startTrialQuery`, no
 * checkout involved); everyone else sees the plain subscribe CTA. One-shot per
 * user: once the trial is granted `status` becomes `trialing`.
 */
export const isTrialEligible = (
  sub:
    | {
        status?: SubscriptionStatusEnum | null;
        source?: SubscriptionSourceEnum | null;
        expiredAt?: Date | string | null;
        stripeSubscriptionId?: string | null;
        storeTransactionId?: string | null;
      }
    | null
    | undefined,
): boolean => {
  if (!sub) return false;
  return !sub.status && !sub.source && !sub.expiredAt && !sub.stripeSubscriptionId && !sub.storeTransactionId;
};

/**
 * A `trialing` row with no billing provider behind it is a card-less web trial
 * granted locally by `startTrialQuery`. Nothing external (webhook) will ever
 * flip its status when the trial lapses, so access must gate on `expiredAt`.
 */
const isLocalTrial = (sub: {
  status?: SubscriptionStatusEnum | null;
  source?: SubscriptionSourceEnum | null;
  stripeSubscriptionId?: string | null;
}): boolean => sub.status === "trialing" && !sub.source && !sub.stripeSubscriptionId;

/**
 * Full access check, including grandfathered legacy Stripe subscribers. New
 * (RevenueCat) subscriptions gate purely on status via {@link hasActiveAccess}.
 * But pre-existing `source: "stripe"` rows may carry a non-active/null status
 * (they're never re-synced by the Stripe webhook unless something changes), so
 * the status-only gate would lock them out. Honor any Stripe-sourced row that
 * hasn't been reset to `lite` or explicitly canceled.
 */
export const hasSubscriptionAccess = (
  sub:
    | {
        status?: SubscriptionStatusEnum | null;
        source?: SubscriptionSourceEnum | null;
        plan?: PlanEnum | null;
        stripeSubscriptionId?: string | null;
        expiredAt?: Date | string | null;
      }
    | null
    | undefined,
): boolean => {
  if (!sub) return false;
  if (hasActiveAccess(sub.status)) {
    // Provider-billed rows gate on status alone (webhooks maintain it); local
    // card-less trials expire by wall clock. `expiredAt` may be a Date (server
    // row) or an ISO string (JSON payload on the client).
    if (!isLocalTrial(sub)) return true;
    return !!sub.expiredAt && new Date(sub.expiredAt).getTime() > Date.now();
  }
  // Grandfather legacy Stripe subscribers. Identify them by `source === "stripe"`
  // OR a lingering `stripeSubscriptionId` (source tracking was added later, so
  // some old rows have the id but a null source). Exclude rows that were reset to
  // `lite` or explicitly canceled — those genuinely lost access.
  const isStripe = sub.source === "stripe" || !!sub.stripeSubscriptionId;
  return isStripe && sub.plan !== "lite" && sub.status !== "canceled";
};

export const isUserLimitExceeded = (plan: PlanEnum, current: number): boolean => current >= planLimits.user[plan];

export const isInvoiceLimitExceeded = (plan: PlanEnum, current: number): boolean => current >= planLimits.invoice[plan];

export const isBillLimitExceeded = (plan: PlanEnum, current: number): boolean => current >= planLimits.expense[plan];

export const isRelationLimitExceeded = (plan: PlanEnum, current: number): boolean =>
  current >= planLimits.relation[plan];

export const isOrganizationLimitExceeded = (plan: PlanEnum, current: number): boolean =>
  current >= planLimits.organization[plan];

export const getPlanUserLimit = (plan: PlanEnum): number => planLimits.user[plan];

export const isDecreasedPlan = (currentPlan: PlanEnum, newPlan: PlanEnum): boolean => {
  const planOrder: PlanEnum[] = ["lite", "plus", "premium", "enterprise"];
  return planOrder.indexOf(newPlan) < planOrder.indexOf(currentPlan);
};
